<template>
  <div class="bg-white px-3 py-16">
    <div class="mx-auto max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-7xl">
      <!-- Header -->
      <div class="mb-8">
        <h2 class="text-2xl lg:text-3xl font-extrabold text-gray-900 mb-2">
          {{ content.title }}
        </h2>
        <p class="text-gray-600">
          {{ content.subtitle }}
          <a
            v-if="content.linkText"
            :href="content.linkUrl || localePath({ name: 'blogPage' })"
            class="text-primary hover:text-primary-darker font-semibold ml-1"
          >
            {{ content.linkText }} &rarr;
          </a>
        </p>
      </div>

      <!-- Tabs -->
      <div class="flex gap-2 mb-8">
        <JoszakiPill
          v-for="tab in content.tabs"
          :key="tab.key"
          class="cursor-pointer select-none px-4 py-1.5 text-sm"
          :inverted="activeTab !== tab.key"
          type="info"
          hoverable
          @click.native="activeTab = tab.key"
        >
          {{ tab.label }}
        </JoszakiPill>
      </div>

      <!-- Blog cards grid -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-6">
        <component
          :is="article.url ? 'a' : 'div'"
          v-for="(article, index) in activeArticles"
          :key="index"
          :href="article.url || undefined"
          class="rounded-lg border border-blue-100 overflow-hidden bg-white hover:shadow-md transition-shadow no-underline"
        >
          <!-- Image or placeholder -->
          <div
            class="bg-blue-100 rounded-t-lg aspect-video flex items-center justify-center overflow-hidden"
          >
            <img
              v-if="article.imageUrl"
              v-lazy-load :data-src="article.imageUrl"
              :alt="article.title"
              class="w-full h-full object-cover"
            />
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="w-12 h-12 text-blue-300"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M2 18h20v2H2z" />
              <path d="M4 18v-2a8 8 0 0 1 16 0v2" />
              <path d="M6 12V9a6 6 0 0 1 12 0v3" />
              <circle cx="12" cy="5" r="2" />
              <path d="M9 18v-4h6v4" />
            </svg>
          </div>

          <!-- Article content -->
          <div class="p-3">
            <p class="text-sm font-semibold text-gray-800 line-clamp-2">
              {{ article.title }}
            </p>
            <p
              v-if="article.description"
              class="text-xs text-gray-500 mt-1 line-clamp-2"
            >
              {{ article.description }}
            </p>
          </div>
        </component>
      </div>

      <!-- CTA button -->
      <div v-if="content.ctaText" class="flex justify-center mt-10">
        <JoszakiButton
          type="info"
          outline
          :href="content.ctaUrl || undefined"
          :to="content.ctaUrl ? undefined : localePath({ name: 'blogPage' })"
        >
          {{ content.ctaText }} &rarr;
        </JoszakiButton>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, useContext } from "@nuxtjs/composition-api";
import useModuleContent from "~/composables/useModuleContent";

export default {
  name: "BlogModule",
  props: {
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { $posthog } = useContext();
    const { content } = useModuleContent(props);

    onMounted(() => {
      $posthog?.capture("front_page_module_viewed", {
        module_slug: props.moduleConfig.slug,
        module_position: props.moduleConfig.position,
        module_row: props.moduleConfig.row,
        version_id: props.moduleConfig.versionId,
      });
    });

    const activeTab = ref(
      content.value.tabs?.[0]?.key || "felujitas"
    );

    const activeArticles = computed(() => {
      return (content.value.articles || []).filter(
        (a) => a.tab === activeTab.value
      );
    });

    return {
      content,
      activeTab,
      activeArticles,
    };
  },
};
</script>
